import React, { useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';

import { BoxedDiv, BoxedSpan } from 'psims/react/components/layout';
import { H2, H3 } from 'psims/react/components/typography';
import { ActionCellTD, ActionCellTH, ColumnHeader, Table, TD, TH, TR } from 'psims/react/pages/primary-pages/data-submissions/shared/data-table-components';
import { UseImporterAnnualActivities } from './use-importer-annual-activities';
import Input from 'psims/react/pages/primary-pages/data-submissions/shared/input';
import Checkbox from 'psims/react/components/checkbox';
import { UpdateMsoAnnualActivityImporterField } from 'psims/models/submission-types/mso/annual-activity/importer/update-mso-annual-activity-importer';
import { UseFocusedField } from 'psims/react/util/use-focused-field';
import { FocusField, isDataFocusField, isDeleteProductFocusField, MsoProductView } from '../../shared/types';
import { UseConfirm } from '../../../shared/use-confirm';
import { recordActionFromEnum } from 'psims/models/api/data-submission-record-action';
import useFocusable from 'psims/react/util/use-focusable';
import { IconButton } from 'psims/react/components/button';
import VisuallyHidden from 'psims/react/components/visually-hidden';
import FloatingMessage from 'psims/react/components/floating-message';
import { EXPIRED_OR_INTERNAL_PRODUCT } from 'psims/constants/validation-messages';
import { TooltipHelp } from '../../../shared/tooltip-help';
import { isEmpty } from 'psims/lib/empty';
import styled from 'styled-components';
import { MsoTargetDayVM } from 'psims/gen/xapi-client/models/MsoTargetDayVM';
import { computeIndicativeMSO } from './shared';

interface AnnualActivitiesEditorProps {
  annualActivitiesCtrl: UseImporterAnnualActivities;
  deleteCtrl: UseConfirm;
  focusFieldCtrl: UseFocusedField<FocusField>;
  forceErrors: boolean;
  isDisabled: boolean;
  products: Array<MsoProductView>;
  msoTargetDays: Array<MsoTargetDayVM>;
}
const TableWrapper = styled.div`
  overflow-x: auto;
  width: auto;
  overflow-y:auto;
  h3{
   font-size: 16px !important;
  }
`;
const CompactTableWrapper = styled(TableWrapper)`
  table {
    border-collapse: collapse;
  }
  th {
    padding: 10px 10px;
  }
  td {
    padding: 4px 8px;
    
  }

  input, textarea, select {
    font-size: 14px;
    height: 28px;
    border: solid 1px silver;
  }

  textarea {
    min-height: 48px;
  }
   .compact-table td {
  vertical-align: middle;
}
.compact-table textarea {
  height: 28px !important;
}
.compact-table input {
  line-height: 1.2;
} 
thead th {
  position: sticky;
  top: 0;
  height:60px;
  background: #f5f5f5;
  border-top: solid 1px silver;
}
`;
const InfoBox = styled.div`
  background: #f5f7fa;
  border: 1px solid #d6dde6;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 20px;
  color: #333;

  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.5;
  }
`;
const PSIMSCard = styled.div`
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  padding: 16px;
  margin-bottom: 20px;
  border: 1px solid #eee;
 
`;
const InfoBoxLight = styled.div`
  background: #ffffff;
  border: 1px solid #fafafa;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 20px;
  color: #333;

  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.5;
  }
`;
// Temporary hardcoding till msotarget table event is added by HE, then will psuh it into api 
{/*const MSO_TARGET_DAYS: Record<number, { noOfDays: number; fuelTypeThreshold: number }> = {
  18: { noOfDays: 90,  fuelTypeThreshold: 200 },
  19: { noOfDays: 150, fuelTypeThreshold: 200 },
  20: { noOfDays: 67,  fuelTypeThreshold: 200 },
  21: { noOfDays: 90,  fuelTypeThreshold: 200 },
};*/}
const AnnualActivitiesEditor = (props: AnnualActivitiesEditorProps) => {
  const vm = useAnnualActivitiesEditorVM(props);

  return (
    <PSIMSCard>
    <BoxedDiv box={{flex: 'column'}}>

      <BoxedDiv box={{marginTop: 0}}>
        <BoxedDiv box={{ marginTop: 0,  padding:0.5 }}>
          <H2>Total imports volumes for self (based off N10 and N30 forms)</H2>
         <InfoBoxLight> 
          <p>{`These volumes should be most (if not all) of the imports you have undertaken for the ${2026} calendar year. The only volumes of product you do not report in this section are those undertaken on behalf of another entity (as per section 5 of the MSO Rules), where they are considered the true importer of the product. These volumes will count towards your indicative MSO calculation at the bottom of the page.`}</p>
          </InfoBoxLight>
        </BoxedDiv>
       
        <BoxedDiv box={{ marginBottom: 0.5,  padding:0.5 }}>
           <H3 style={{marginLeft:'20px'}}>MSO annual activity</H3>
          <InfoBoxLight><p>Your total annual import volume is automatically calculated by adding Imported by another entity on your behalf plus Total import volume (for self). </p>
        <p>
          This section allows you to fill in the total import volumes for the previous calendar year, which will be used to set your future MSO quantities. Importing volumes (for self) must meet the definition under section 5 of the <em>Fuel Security Act 2022</em> and section 5(3) of the <em>Fuel Security (Minimum Stockholding Obligation) Rules 2022</em>. The 'Total import volume (by other entities)' and 'Total import volume (on behalf of)' columns will be populated from the data entered in the tables below. 
        </p>

        <p>
          Quantities should be reported to the nearest <strong>megalitre (ML)</strong>.
        </p>
        </InfoBoxLight>
        </BoxedDiv>
      </BoxedDiv>

      <BoxedDiv box={{marginTop: 2}}>
        
         <CompactTableWrapper> 
        <Table caption='Data for annual activities' customWidth='fit-content'>
          <thead>
            <TR>
              <ColumnHeader label='MSO Product' fixedWidth='230px' />

              <ColumnHeader
                $align='center'
                label='Total import volume (for self)'
                fixedWidth='230px'
                Help={
                  <div>
                    <p>The total volumes you have imported for your entity (exclude volumes that are either imported for another entity or on behalf of another entity (for section 19F(2)(e) of the POFR Rules)).</p>
                    <p>Note: Importing volumes must meet the definition under section 5 of the <em>Fuel Security Act 2022</em> and section 5(3) of the <em>Fuel Security (Minimum Stockholding Obligation) Rules 2022</em>.</p>
                    <p>Quantities should be reported to the nearest <strong>megalitre (ML)</strong>.</p>
                  </div>
                }
              />

              {/* <ColumnHeader
                $align='center'
                label='Total import volume (by other entities)'
                fixedWidth='230px'
                Help={<p>This figure will be populated from the data entered in the tables below. It should correspond to the total volume that has been imported by another entity/entities for you (for section 19F(2)(e) of the POFR Rules).</p>}
              />

              <ColumnHeader
                $align='center'
                label='Total import volume (on behalf of)'
                fixedWidth='230px'
                Help={<p>This figure will be populated from the data entered in the tables below. It should correspond to the total volume that you have imported on behalf of another entity (for section 19F(2)(e) of the POFR Rules).</p>}
              /> */}

              <ColumnHeader
                $align='center'
                label='Total annual import volume (ML)'
                fixedWidth='230px' 
                Help={<>
                  <p>The aggregate importing volumes for your entity. </p>
                  <p>The volume is calculated using the formula: Total annual volume = total import volume (for self) + total import volume (by other entities).</p>
                </>}
              />

              <ColumnHeader $align='center'
                label='Indicative MSO based on volumes provided'
                fixedWidth='230px'
                Help='The total storage capacity for each MSO product for your entity (section 19F(2)(c) of the POFR Rules) at a point in time at the end of the calendar year.'
              />

              {/* <ColumnHeader
                label='Enforceable arrangements?'
                fixedWidth='330px'
                Help={<div>
                  <p>
                    In the past 12 months, did you use an enforceable arrangement for any MSO product under section 23 or section 24 of the <em>Fuel Security Act 2022</em> to help meet your MSO?
                  </p>
                  <p>
                    If yes, please upload the details as per paragraph 19F(2)(e) of the MSO Rules to the system.
                  </p>
                </div>}
              /> */}

              {
                vm.hasExpiredData ?
                <ActionCellTH>
                  <VisuallyHidden>Actions</VisuallyHidden>
                </ActionCellTH> :
                null
              }
            </TR>
          </thead>

          <tbody>
            {
              vm.data.map((data, index) => (
                <DataRow
                  annualActivitiesCtrl={vm.annualActivitiesCtrl}
                  data={data}
                  deleteCtrl={vm.deleteCtrl}
                  focusFieldCtrl={vm.focusFieldCtrl}
                  forceErrors={vm.forceErrors}
                  index={index}
                  isDisabled={vm.isDisabled}
                  msoProduct={vm.products.find(p => p.id === data.data.msoProductId) as MsoProductView}
                  msoTargetDays={vm.msoTargetDays}
                  key={index}
                />
              ))
            }
          </tbody>

        </Table>
        </CompactTableWrapper>
       
      </BoxedDiv>
    </BoxedDiv>
    </PSIMSCard> 
  );
}
/*
function computeIndicativeMso(
  totalAnnualVolume: number | null | undefined,
  msoProductId: number,
  msoTargetDays: Array<MsoTargetDayVM>
): number | null {
  const config = msoTargetDays.find(t => t.msoProductId === msoProductId);
  if (!config || totalAnnualVolume == null) return null;

  const { noOfDays, fuelTypeThreshHold } = config;

  if (totalAnnualVolume <= fuelTypeThreshHold) return null;

  return Math.ceil(totalAnnualVolume * (noOfDays / 365));
}*/

function useAnnualActivitiesEditorVM ({
  annualActivitiesCtrl,
  deleteCtrl,
  focusFieldCtrl,
  forceErrors,
  isDisabled,
  products,
  msoTargetDays
}: AnnualActivitiesEditorProps) {

  const data = useMemo(() => {
    return annualActivitiesCtrl.updateAnnualActivities
      .filter(a => products.some(p => p.id === a.data.msoProductId))
  }, [annualActivitiesCtrl.updateAnnualActivities, products]);

  const hasExpiredData = useMemo(() => {
    return !isDisabled && products.some(p => {
      return data.find(d => (
        d.data.msoProductId === p.id &&
        p.productStatus === 'inactive' &&
        recordActionFromEnum(d.data.recordAction) !== 'Delete'
      ));
    });
  }, [data, isDisabled, products]);

  return {
    annualActivitiesCtrl,
    data,
    deleteCtrl,
    focusFieldCtrl,
    forceErrors,
    hasExpiredData,
    isDisabled,
    products,
    msoTargetDays
  }
}

type UpdateAnnualActivitiesDraft = UseImporterAnnualActivities['updateAnnualActivities'][number];

interface DataRowProps {
  annualActivitiesCtrl: UseImporterAnnualActivities;
  data: UpdateAnnualActivitiesDraft;
  deleteCtrl: UseConfirm;
  focusFieldCtrl: UseFocusedField<FocusField>;
  forceErrors: boolean;
  index: number;
  isDisabled: boolean;
  msoProduct: MsoProductView;
  msoTargetDays: Array<MsoTargetDayVM>;
}

const DataRow = (props: DataRowProps) => {
  const vm = useDataRowVM(props);

  if (vm.hideProduct) {
    return null;
  }

  return <TR>
    <TH scope='row' verticalAlign='middle'>
      <BoxedSpan box={{alignItems: 'center', flex: 'row'}}>
        {vm.msoProduct.name}

        {
          vm.showRowError ?
            <BoxedSpan box={{ marginBottom: -0.5, marginLeft: 1 }}>
              <FloatingMessage
                content={EXPIRED_OR_INTERNAL_PRODUCT}
                kind="warning"
                role="alert"
              />
            </BoxedSpan> :
            !isEmpty(vm.msoProduct.Help?.content) ?
            <TooltipHelp
              Help={<ReactMarkdown>{vm.msoProduct.Help?.content as string}</ReactMarkdown>}
            /> :
            null
        }
      </BoxedSpan>
    </TH>

    <TD>
      <Input
        disabled={vm.isDisabled}
        error={vm.validations.totalImportVolumeSelf?.inline}
        forceError={vm.forceErrors}
        kind='number'
        label='Total import volume (for self)'
        onChange={val => vm.updateField('totalImportVolumeSelf', val)}
        shouldFocus={vm.rowFocusField?.field === 'totalImportVolumeSelf'}
        value={vm.data.totalImportVolumeSelf}
      />
    </TD>

    {/* <TD>
      <Input
        disabled={true}
        error={vm.validations.totalImportVolumeOthers?.inline}
        forceError={vm.forceErrors}
        info={vm.infos.totalImportVolumeOthers?.inline}
        kind='number'
        label='Total import volume (by other entities)'
        onChange={() => {}}
        shouldFocus={vm.rowFocusField?.field === 'totalImportVolumeOthers'}
        value={vm.data.totalImportVolumeOthers}
      />
    </TD>

    <TD>
      <Input
        disabled={true}
        error={vm.validations.totalImportVolumeOnBehalf?.inline}
        forceError={vm.forceErrors}
        info={vm.infos.totalImportVolumeOnBehalf?.inline}
        kind='number'
        label='Total import volume (on behalf of)'
        onChange={() => {}}
        shouldFocus={vm.rowFocusField?.field === 'totalImportVolumeOnBehalf'}
        value={vm.data.totalImportVolumeOnBehalf}
      />
    </TD> */}

    <TD>
      <Input
        disabled={true}
        info={vm.infos.totalAnnualVolume?.inline}
        kind='number'
        label='Total annual import volume (ML)'
        onChange={() => {}}
        shouldFocus={false}
        value={vm.totalAnnualVolume}
      />
    </TD>

    <TD>
      <Input
        disabled={true}
        kind='number'
        label='Indicative MSO based on volumes provided'
        onChange={() => {}}
        shouldFocus={false}
        value={vm.indicativeMso != null ? vm.indicativeMso : undefined}
      />
    </TD>
      {/**made it readonly */}
    {/* <TD>
      <Checkbox
        disabled={vm.isDisabled}
        label='Enforceable arrangements'
        name={`enforceable_arrangements_${vm.index}`}
        onChange={val => vm.updateField('enforceableArrangements', val)}
        value={vm.data.enforceableArrangements || false}
      />
    </TD> */}

    {
      vm.showDeleteButton ?
      <ActionCellTD>
        <DeleteProductButton
          focusedField={vm.focusFieldCtrl.focusedField}
          onClick={() => vm.deleteCtrl.requestConfirmation(
            'Are you sure you wish to delete this data?',
            'This annual activity record will be deleted.',
            () => vm.annualActivitiesCtrl.deleteAnnualActivityByProductId(vm.msoProduct.id)
          )}
          productId={vm.msoProduct.id}
        />
      </ActionCellTD> :
      null
    }
  </TR>
}

function useDataRowVM({
  annualActivitiesCtrl,
  data,
  deleteCtrl,
  focusFieldCtrl,
  forceErrors,
  index,
  isDisabled,
  msoProduct,
  msoTargetDays
}: DataRowProps) {
  const rowFocusField = useMemo(() => {
    return isDataFocusField(focusFieldCtrl.focusedField) && focusFieldCtrl.focusedField?._id === data.data._id ? focusFieldCtrl.focusedField : null
  }, [data.data._id, focusFieldCtrl]);

  const indicativeMso = useMemo(() => {
 // console.log('msoTargetDays in DataRow:', msoTargetDays);
 // console.log('totalAnnualVolume:', data.totalAnnualVolume);
  //console.log('msoProductId:', data.data.msoProductId);
  return computeIndicativeMSO(data.totalAnnualVolume, data.data.msoProductId, msoTargetDays);
}, [data.totalAnnualVolume, data.data.msoProductId, msoTargetDays]);

  const updateField = useCallback(
  <TField extends UpdateMsoAnnualActivityImporterField, TVal extends UpdateAnnualActivitiesDraft['data'][TField]>
  (field: TField, val: TVal) => {
    const updatedData = {
      ...data.data,
      [field]: val,
    };

    // Recompute totalAnnualVolume and indicativeMSO with the updated field value
    const newTotalSelf = field === 'totalImportVolumeSelf'
      ? (val as number)
      : (data.data.totalImportVolumeSelf ?? 0);

    const newTotalAnnual = newTotalSelf + (data.data.totalImportVolumeOthers ?? 0);

    const newIndicativeMso = computeIndicativeMSO(
      newTotalAnnual,
      data.data.msoProductId,
      msoTargetDays
    );

    annualActivitiesCtrl.updateAnnualActivity({
      ...updatedData,
      indicativeMSOBasedVolume: newIndicativeMso ?? undefined,
    });
  },
  [data, annualActivitiesCtrl, msoTargetDays]
);

  const hasExpiredData = useMemo(() => {
    return msoProduct.productStatus === 'inactive' && recordActionFromEnum(data.data.recordAction) !== 'Delete';
  }, [msoProduct, data])

  const hideProduct = useMemo(() => {
    return msoProduct.productStatus === 'inactive' && data.data.id == null;
  }, [msoProduct, data.data.id]);

  const showRowError = useMemo(() => {
    return hasExpiredData && !isDisabled;
  }, [hasExpiredData, isDisabled]);

  const showDeleteButton = useMemo(() => {
    return hasExpiredData && !isDisabled
  }, [hasExpiredData, isDisabled]);

  return {
    annualActivitiesCtrl,
    data: data.data,
    deleteCtrl,
    focusFieldCtrl,
    forceErrors,
    hideProduct,
    index,
    infos: data.validationAlerts,
    isDisabled,
    msoProduct,
    rowFocusField,
    showDeleteButton,
    showRowError,
    totalAnnualVolume: data.totalAnnualVolume,
    updateField,
    validations: data.validations,
    indicativeMso,
    msoTargetDays
  }
}

interface DeleteProductProps {
    focusedField: unknown;
    onClick: () => any;
    productId: number;
}

const DeleteProductButton = React.forwardRef(({ focusedField, onClick, productId }: DeleteProductProps, ref) => {
    const focusable = useFocusable({
        setFocused: (
            isDeleteProductFocusField(focusedField) &&
            focusedField.kind === 'annualActivities' &&
            focusedField.productId === productId
        ),
    });

    const setRef = useCallback((el: HTMLElement | null) => {
        if (typeof ref === 'function') {
            ref(el);
        }
        focusable.setRef(el);
    }, [focusable, ref]);

    return (
        <BoxedSpan box={{marginLeft: 1}}>
          <IconButton
              color='dark-grey'
              icon='trash'
              label='Delete row for product'
              onClick={onClick}
              ref={setRef}
              size='sm'
          />
        </BoxedSpan>
    );
});

export default AnnualActivitiesEditor;
